/* ------------------------------------------------------------------
  File: appConfig.js
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-10
------------------------------------------------------------------ */

// Purpose:
//   - appConfig defines frontend application-level behavior and settings.
//   - It centralizes layout defaults, UI behavior, pagination settings,
//     animation settings, feature flags, legal display metadata,
//     developer credits, and formatting preferences.
//   - It should not contain page content, route paths, SEO metadata,
//     or core company identity information.

import { siteConfig } from "./siteConfig";

const currentYear = new Date().getFullYear();

export const appConfig = {
  app: {
    name: "Accend Holdings Website",
    version: "1.0.0",
    environment: process.env.NODE_ENV || "development",
  },

  credits: {
    primaryDeveloper: {
      name: "Engr. Shaffa K. Kokro Jr.",
      title: "CEO, Accend Holdings Ltd.",
      company: siteConfig.name,
    },

    contributors: [
      // Add project contributors here when needed.
      // Example:
      // {
      //   name: "Developer Name",
      //   role: "Frontend Developer",
      //   company: "Independent Contractor",
      //   contribution: "UI implementation and component development",
      // }
    ],

    agencies: [
      // Add external agencies or partner studios here when needed.
      // Example:
      // {
      //   name: "Agency Name",
      //   role: "Design Partner",
      //   website: "https://example.com",
      // }
    ],
  },

  legal: {
    owner: siteConfig.legalName,

    copyright: {
      startYear: 2026,
      currentYear,
      text: `© 2026${currentYear > 2026 ? `-${currentYear}` : ""} ${
        siteConfig.legalName
      }. All rights reserved.`,
    },

    license: {
      type: "Proprietary",
      notice:
        "This website, its source code, design system, content structure, and related assets are proprietary to Accend Holdings Ltd. Unauthorized copying, modification, distribution, or reuse is prohibited without written permission.",
    },
  },

  layout: {
    defaultLayout: "main",

    header: {
      sticky: true,
      transparentOnHome: true,
      hideOnScroll: false,
    },

    footer: {
      showNewsletter: true,
      showSocialLinks: true,
      showLegalLinks: true,
    },

    container: {
      maxWidth: "1200px",
      wideMaxWidth: "1440px",
    },
  },

  navigation: {
    dropdownTrigger: "hover",
    closeOnRouteChange: true,
    mobileBreakpoint: 992,
    showBackToTop: true,
  },

  pagination: {
    defaultPageSize: 9,
    newsroomPageSize: 9,
    reportsPageSize: 6,
    careersPageSize: 8,
    searchPageSize: 10,
  },

  content: {
    defaultExcerptLength: 160,
    defaultDateFormat: "MMM DD, YYYY",
    fallbackImage: "/assets/images/placeholders/default-placeholder.jpg",
  },

  forms: {
    validateOnBlur: true,
    validateOnChange: false,
    resetAfterSubmit: true,
    showRequiredAsterisk: true,
  },

  animations: {
    enabled: true,
    duration: 300,
    easing: "ease-in-out",
    revealOnScroll: true,
  },

  features: {
    enableSearch: true,
    enableNewsletter: true,
    enableCareersApply: true,
    enableInvestorInquiry: true,
    enableCookieBanner: true,
    enableDarkMode: false,
    enableAnalytics: true,
  },

  analytics: {
    provider: "google-analytics",
    trackingId: process.env.REACT_APP_GA_TRACKING_ID || "",
    enabled:
      process.env.NODE_ENV === "production" &&
      Boolean(process.env.REACT_APP_GA_TRACKING_ID),
  },
};